import React, { useState, useMemo, useEffect } from 'react';
import Layout from '../Layout';
import Plot from 'react-plotly.js';
import { analyzeNgram, analyzeNgramFromAPI, checkMissingDaysNgram, getCachedAnalysis } from '../../services/trackingService';
import 'bootstrap/dist/css/bootstrap.min.css';
import alertify from 'alertifyjs';
import 'alertifyjs/build/css/alertify.css';

const STORAGE_KEY = 'ngram_analysis';
let cache = null;

/* ──────────────────────────────── */
/* N-gram Analysis — enhanced port of tracking2/ngram.py */
/* ──────────────────────────────── */

const METRIC_OPTIONS = ['Impressions', 'Clicks', 'Orders', 'Spend', 'Sales', 'CTR', 'CVR', 'CPC', 'RPC', 'ACOS'];
const metricKey = (m) => m.toLowerCase();

function NgramAnalysis() {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        async function load() {
            try {
                const saved = cache || JSON.parse(sessionStorage.getItem(STORAGE_KEY));
                if (saved) {
                    setRawTable(saved.rawTable || []);
                    setAsins(saved.asins || []);
                    if (saved.selectedAsin) setSelectedAsin(saved.selectedAsin);
                    if (saved.dataSource) setDataSource(saved.dataSource);
                    if (saved.startDate) setStartDate(saved.startDate);
                    if (saved.endDate) setEndDate(saved.endDate);
                    if (saved.nValues) setNValues(saved.nValues);
                    if (saved.minCharLen) setMinCharLen(saved.minCharLen);
                    if (saved.excludeText !== undefined) setExcludeText(saved.excludeText);
                    if (saved.minImpressions !== undefined) setMinImpressions(saved.minImpressions);
                    if (saved.minClicks !== undefined) setMinClicks(saved.minClicks);
                    if (saved.minOrders !== undefined) setMinOrders(saved.minOrders);
                    return;
                }
                const profileId = localStorage.getItem('selectedAccountValue');
                if (profileId) {
                    const backend = await getCachedAnalysis(profileId, 'ngram');
                    if (backend.success && backend.data) {
                        const d = backend.data;
                        setRawTable(d.table || []);
                        setAsins(d.asins || []);
                    }
                }
            } catch (e) {}
        }
        load();
    }, []);

    // Dual-mode: 'file' or 'api'
    const [dataSource, setDataSource] = useState('file');
    const [startDate, setStartDate] = useState(() => {
        const d = new Date(); d.setDate(d.getDate() - 30);
        return d.toISOString().slice(0, 10);
    });
    const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [statusMsg, setStatusMsg] = useState('');

    // Raw results from backend
    const [rawTable, setRawTable] = useState([]);
    const [asins, setAsins] = useState([]);

    // N-gram settings
    const [selectedAsin, setSelectedAsin] = useState('');
    const [nValues, setNValues] = useState([1, 2, 3]);
    const [minCharLen, setMinCharLen] = useState(2);
    const [excludeText, setExcludeText] = useState('');

    // Active N filter (post-load)
    const [activeN, setActiveN] = useState(null);

    // Metric filters
    const [minImpressions, setMinImpressions] = useState(100);
    const [minClicks, setMinClicks] = useState(5);
    const [minOrders, setMinOrders] = useState(0);
    const [cvrRange, setCvrRange] = useState([0, 100]);
    const [rpcRange, setRpcRange] = useState([0, 100]);
    const [cpcRange, setCpcRange] = useState([0, 100]);
    const [acosRange, setAcosRange] = useState([0, 200]);

    // Scatter plot axes
    const [xMetric, setXMetric] = useState('Clicks');
    const [yMetric, setYMetric] = useState('CVR');
    const [sizeMetric, setSizeMetric] = useState('Impressions');

    // Table collapse
    const [showTable, setShowTable] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (dataSource === 'file' && !file) return;
        if (dataSource === 'api') {
            const profileId = localStorage.getItem('selectedAccountValue');
            if (!profileId) {
                setError('Please select an Amazon Ads account in the header before using API mode.');
                return;
            }
        }
        setLoading(true);
        setError('');
        try {
            const excludeContains = excludeText
                .split(',')
                .map(s => s.trim().toLowerCase())
                .filter(Boolean);

            let res;
            
            const executeAnalysis = async () => {
                setLoading(true);
                setStatusMsg(dataSource === 'api' ? '🔄 Fetching from Amazon API — this may take a few minutes...' : '🔄 Processing file...');
                try {
                    if (dataSource === 'api') {
                        const profileId = localStorage.getItem('selectedAccountValue');
                        res = await analyzeNgramFromAPI(profileId, startDate, endDate, {
                            asin: selectedAsin || undefined,
                            nValues,
                            minCharLen,
                            excludeContains,
                        });
                    } else {
                        res = await analyzeNgram(file, {
                            asin: selectedAsin || undefined,
                            nValues,
                            minCharLen,
                            excludeContains,
                        });
                    }
                    const tbl = res.data.table || [];
                    const asinList = res.data.asins || [];
                    setRawTable(tbl);
                    setAsins(asinList);
                    setActiveN(null);

                    // Set dynamic slider maxes from data
                    if (tbl.length > 0) {
                        const maxRpc = Math.max(...tbl.map(r => r.rpc || 0), 1);
                        const maxCpc = Math.max(...tbl.map(r => r.cpc || 0), 1);
                        const maxAcos = Math.max(100, ...tbl.map(r => r.acos || 0));
                        setRpcRange([0, Math.ceil(maxRpc * 100) / 100]);
                        setCpcRange([0, Math.ceil(maxCpc * 100) / 100]);
                        setAcosRange([0, Math.ceil(maxAcos * 100) / 100]);
                        setCvrRange([0, 100]);
                        setMinImpressions(100);
                        setMinClicks(5);
                        setMinOrders(0);
                    }

                    if (tbl.length === 0) {
                        setError(
                            'No N-grams found. Please make sure the file is an Amazon Ads Search Terms Report ' +
                            'with columns such as "Customer Search Term", "Impressions", "Clicks", "Spend", "Sales".'
                        );
                        alertify.confirm(
                            'Insufficient Data',
                            'Not enough data found. Would you like to request a broader date range from the Amazon API?',
                            function () {
                                setDataSource('api');
                                alertify.message('Please select a new date range and analyze again.');
                            },
                            function () {
                                alertify.error('Analysis canceled.');
                            }
                        ).set('labels', {ok: 'Yes, fetch from API', cancel: 'Cancel'});
                    } else {
                        try {
                            const data = {
                                rawTable: tbl,
                                asins: asinList,
                                selectedAsin,
                                dataSource,
                                startDate,
                                endDate,
                                nValues,
                                minCharLen,
                                excludeText,
                                minImpressions,
                                minClicks,
                                minOrders,
                            };
                            cache = data;
                            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
                        } catch (e) {
                            if (cache) cache.rawTable = tbl;
                        }

                        // Notificación Modal Sutil
                        alertify.success('Data loaded and N-gram charts generated successfully!');
                    }
                } catch (err) {
                    setError(err.response?.data?.message || err.message);
                    setStatusMsg('');
                } finally {
                    setLoading(false);
                }
            };

            if (dataSource === 'api') {
                const profileId = localStorage.getItem('selectedAccountValue');
                try {
                    setStatusMsg('⏳ Checking local cache...');
                    const checkRes = await checkMissingDaysNgram(profileId, startDate, endDate);
                    if (checkRes.missingDays > 0) {
                        setLoading(false);
                        setStatusMsg(`📡 ${checkRes.missingDays} days not found in database — Amazon API needed`);
                        alertify.confirm(
                            'Amazon API Fetch Required',
                            `We need to fetch ${checkRes.missingDays} missing days from Amazon API. This may take a few minutes. Continue?`,
                            function () { executeAnalysis(); },
                            function () { alertify.error('Analysis canceled.'); setStatusMsg(''); }
                        ).set('labels', {ok: 'Yes, fetch from API', cancel: 'Cancel'});
                        return;
                    } else {
                        setStatusMsg('✅ All data found in database — loading locally...');
                    }
                } catch (err) {
                    console.error("Error pre-flight check Ngram", err);
                    setStatusMsg('');
                }
            }

            // Si es archivo o no faltan días en la API, ejecuta directamente
            executeAnalysis();
        } catch (err) {
            setError(err.response?.data?.message || err.message);
            setLoading(false);
        }
    };


    // Client-side filtering
    const filtered = useMemo(() => {
        let data = rawTable;

        // N-gram size filter
        if (activeN) data = data.filter(r => r.n === activeN);

        // Minimum filters
        data = data.filter(r => r.impressions >= minImpressions);
        data = data.filter(r => r.clicks >= minClicks);
        data = data.filter(r => r.orders >= minOrders);

        // Range filters
        data = data.filter(r => r.cvr >= cvrRange[0] && r.cvr <= cvrRange[1]);
        data = data.filter(r => r.rpc >= rpcRange[0] && r.rpc <= rpcRange[1]);
        data = data.filter(r => r.cpc >= cpcRange[0] && r.cpc <= cpcRange[1]);
        data = data.filter(r => {
            const acos = r.acos || 0;
            return acos >= acosRange[0] && acos <= acosRange[1];
        });

        return data;
    }, [rawTable, activeN, minImpressions, minClicks, minOrders, cvrRange, rpcRange, cpcRange, acosRange]);

    // Dynamic slider max values from raw data
    const sliderMaxes = useMemo(() => {
        if (rawTable.length === 0) return { rpc: 100, cpc: 100, acos: 200 };
        return {
            rpc: Math.ceil(Math.max(...rawTable.map(r => r.rpc || 0), 1) * 100) / 100,
            cpc: Math.ceil(Math.max(...rawTable.map(r => r.cpc || 0), 1) * 100) / 100,
            acos: Math.ceil(Math.max(100, ...rawTable.map(r => r.acos || 0)) * 100) / 100,
        };
    }, [rawTable]);

    const toggleN = (n) => {
        setNValues(prev => prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n]);
    };

    // Stepper component
    const Stepper = ({ label, value, onChange, min = 0, step = 1 }) => (
        <div>
            <label className="form-label text-muted small mb-1">{label}</label>
            <div className="input-group input-group-sm">
                <input type="number" className="form-control" value={value}
                    onChange={e => onChange(Number(e.target.value))} min={min} step={step} />
                <button className="btn btn-outline-secondary" type="button"
                    onClick={() => onChange(Math.max(min, value - step))}>−</button>
                <button className="btn btn-outline-secondary" type="button"
                    onClick={() => onChange(value + step)}>+</button>
            </div>
        </div>
    );

    // Range slider component
    const RangeSlider = ({ label, value, onChange, min = 0, max = 100, step = 0.01 }) => (
        <div className="mb-2">
            <label className="form-label text-muted small mb-1 d-flex justify-content-between">
                <span>{label}</span>
                <span className="text-primary">{value[0].toFixed(2)} — {value[1].toFixed(2)}</span>
            </label>
            <div className="d-flex gap-2 align-items-center">
                <input type="range" className="form-range" min={min} max={max} step={step}
                    value={value[0]} onChange={e => onChange([Number(e.target.value), value[1]])} />
                <input type="range" className="form-range" min={min} max={max} step={step}
                    value={value[1]} onChange={e => onChange([value[0], Number(e.target.value)])} />
            </div>
        </div>
    );

    return (
        <Layout>
            <div className="container-fluid py-4">
                <h2 className="fw-bold mb-4">N-gram Analysis</h2>

                {error && <div className="alert alert-danger">{error}</div>}

                {/* ───── Data Source + Upload/API form ───── */}
                <div className="card mb-4">
                    <div className="card-body">
                        {/* Mode Toggle */}
                        <div className="btn-group mb-3" role="group">
                            <button type="button" className={`btn ${dataSource === 'file' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setDataSource('file')}>
                                <i className="bi bi-file-earmark-arrow-up me-1"></i> Upload File
                            </button>
                            <button type="button" className={`btn ${dataSource === 'api' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setDataSource('api')}>
                                <i className="bi bi-cloud-download me-1"></i> Fetch from Amazon API
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="row g-3 align-items-end">
                                {dataSource === 'file' ? (
                                    <div className="col-md-4">
                                        <label className="form-label">Upload Search Term Report</label>
                                        <input type="file" className="form-control" accept=".csv,.xlsx,.xls"
                                            onChange={e => setFile(e.target.files[0])} required />
                                    </div>
                                ) : (
                                    <>
                                        <div className="col-md-3">
                                            <label className="form-label">Start Date</label>
                                            <input type="date" className="form-control" value={startDate}
                                                onChange={e => setStartDate(e.target.value)} required />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label">End Date</label>
                                            <input type="date" className="form-control" value={endDate}
                                                onChange={e => setEndDate(e.target.value)} required />
                                        </div>
                                        <div className="col-md-4">
                                            <div className="alert alert-info py-1 px-2 mb-0 small">
                                                <i className="bi bi-info-circle me-1"></i>
                                                Account: <strong>{localStorage.getItem('selectedAccountName') || 'Not selected'}</strong>
                                            </div>
                                        </div>
                                    </>
                                )}
                                <div className="col-md-2">
                                    <button type="submit" className="btn btn-primary w-100"
                                        disabled={loading || (dataSource === 'file' && !file)}>
                                        {loading ? <span className="spinner-border spinner-border-sm" /> : 'Analyze'}
                                    </button>
                                </div>
                            </div>
                        </form>
                        {/* Status indicator */}
                        {(loading || statusMsg) && (
                            <div className="mt-2 d-flex align-items-center" style={{ minHeight: 24 }}>
                                {loading && <span className="spinner-border spinner-border-sm text-primary me-2" style={{ width: 14, height: 14, borderWidth: 2 }} />}
                                {statusMsg && <small className="text-muted" style={{ fontSize: '0.8rem' }}>{statusMsg}</small>}
                            </div>
                        )}
                    </div>
                </div>

                {/* ───── N-gram Settings ───── */}
                <div className="card mb-4">
                    <div className="card-header fw-semibold">N-gram Settings</div>
                    <div className="card-body">
                        <div className="row g-3 align-items-end">
                            <div className="col-md-3">
                                <label className="form-label">N-gram size</label>
                                <div className="btn-group btn-group-sm w-100">
                                    {[1, 2, 3].map(n => (
                                        <button key={n} type="button" className={`btn ${nValues.includes(n) ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => toggleN(n)}>{n} ×</button>
                                    ))}
                                </div>
                            </div>
                            <div className="col-md-3">
                                <Stepper label="Minimum characters per n-gram"
                                    value={minCharLen} onChange={setMinCharLen} min={1} />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Exclude n-grams containing (comma separated)</label>
                                <input className="form-control form-control-sm" value={excludeText}
                                    onChange={e => setExcludeText(e.target.value)}
                                    placeholder="e.g. brand, test" />
                            </div>
                            {asins.length > 0 && (
                                <div className="col-md-3">
                                    <label className="form-label">Filter by ASIN</label>
                                    <select className="form-select form-select-sm" value={selectedAsin}
                                        onChange={e => setSelectedAsin(e.target.value)}>
                                        <option value="">All ASINs</option>
                                        {asins.map(a => <option key={a} value={a}>{a}</option>)}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* ───── Filters (shown after data loads) ───── */}
                {rawTable.length > 0 && (
                    <div className="card mb-4">
                        <div className="card-header fw-semibold">Filters</div>
                        <div className="card-body">
                            <div className="row g-3">
                                {/* Column 1: Steppers */}
                                <div className="col-md-4">
                                    <Stepper label="Impressions ≥" value={minImpressions}
                                        onChange={setMinImpressions} min={0} step={10} />
                                    <div className="mt-2">
                                        <Stepper label="Clicks ≥" value={minClicks}
                                            onChange={setMinClicks} min={0} step={1} />
                                    </div>
                                    <div className="mt-2">
                                        <Stepper label="Orders ≥" value={minOrders}
                                            onChange={setMinOrders} min={0} step={1} />
                                    </div>
                                </div>
                                {/* Column 2: Range sliders */}
                                <div className="col-md-4">
                                    <RangeSlider label="CVR %" value={cvrRange}
                                        onChange={setCvrRange} min={0} max={100} step={0.5} />
                                    <RangeSlider label="RPC" value={rpcRange}
                                        onChange={setRpcRange} min={0} max={sliderMaxes.rpc} step={0.01} />
                                </div>
                                {/* Column 3: Range sliders */}
                                <div className="col-md-4">
                                    <RangeSlider label="CPC" value={cpcRange}
                                        onChange={setCpcRange} min={0} max={sliderMaxes.cpc} step={0.01} />
                                    <RangeSlider label="ACOS %" value={acosRange}
                                        onChange={setAcosRange} min={0} max={sliderMaxes.acos} step={0.5} />
                                </div>
                            </div>

                            {/* N-gram size filter buttons */}
                            <div className="mt-3">
                                <label className="form-label text-muted small">View n-gram size</label>
                                <div className="btn-group btn-group-sm">
                                    <button className={`btn ${!activeN ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setActiveN(null)}>All</button>
                                    {nValues.map(n => (
                                        <button key={n}
                                            className={`btn ${activeN === n ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setActiveN(n)}>{n}-gram</button>
                                    ))}
                                </div>
                                <span className="ms-3 text-muted small">
                                    {filtered.length} n-grams after filters
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* ───── Scatter Plot ───── */}
                {filtered.length > 0 && (
                    <div className="card mb-4">
                        <div className="card-header fw-semibold">N-gram Scatter Plot</div>
                        <div className="card-body">
                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <label className="form-label">X axis</label>
                                    <select className="form-select form-select-sm" value={xMetric}
                                        onChange={e => setXMetric(e.target.value)}>
                                        {METRIC_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Y axis</label>
                                    <select className="form-select form-select-sm" value={yMetric}
                                        onChange={e => setYMetric(e.target.value)}>
                                        {METRIC_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Bubble size</label>
                                    <select className="form-select form-select-sm" value={sizeMetric}
                                        onChange={e => setSizeMetric(e.target.value)}>
                                        {METRIC_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>
                            </div>

                            <Plot
                                data={(() => {
                                    // Group by N-gram size for coloring
                                    const nSizes = [...new Set(filtered.map(r => r.n))].sort();
                                    const colors = ['#60A5FA', '#3B82F6', '#1E40AF', '#1E3A5F'];
                                    return nSizes.map((n, i) => {
                                        const subset = filtered.filter(r => r.n === n);
                                        const sizeValues = subset.map(r => Math.max(r[metricKey(sizeMetric)] || 1, 1));
                                        return {
                                            x: subset.map(r => r[metricKey(xMetric)] || 0),
                                            y: subset.map(r => r[metricKey(yMetric)] || 0),
                                            text: subset.map(r => r.ngram),
                                            mode: 'markers',
                                            type: 'scatter',
                                            name: `${n}-gram`,
                                            marker: {
                                                size: sizeValues,
                                                sizemode: 'area',
                                                sizeref: 2 * Math.max(...sizeValues) / (40 ** 2),
                                                sizemin: 4,
                                                color: colors[i % colors.length],
                                                opacity: 0.7,
                                            },
                                            hovertemplate:
                                                '<b>%{text}</b><br>' +
                                                `${xMetric}: %{x}<br>` +
                                                `${yMetric}: %{y}<br>` +
                                                '<extra></extra>',
                                        };
                                    });
                                })()}
                                layout={{
                                    title: { text: 'N-gram Performance' },
                                    height: 450,
                                    xaxis: { title: { text: xMetric } },
                                    yaxis: { title: { text: yMetric } },
                                    margin: { l: 60, r: 20, t: 50, b: 50 },
                                    legend: { orientation: 'h', y: -0.15 },
                                    hovermode: 'closest',
                                }}
                                config={{ responsive: true }}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                )}

                {/* ───── Top 20 Bar Chart ───── */}
                {filtered.length > 0 && (
                    <div className="card mb-4">
                        <div className="card-body">
                            <h5>Top 20 N-grams by Impressions</h5>
                            <Plot
                                data={[{
                                    x: filtered.slice(0, 20).map(r => r.ngram),
                                    y: filtered.slice(0, 20).map(r => r.impressions),
                                    type: 'bar',
                                    marker: { color: '#3B82F6' },
                                    hovertemplate: '%{x}<br>Impressions: %{y:,}<br>Clicks: %{customdata[0]:,}<br>ACOS: %{customdata[1]:.2f}%<extra></extra>',
                                    customdata: filtered.slice(0, 20).map(r => [r.clicks, r.acos]),
                                }]}
                                layout={{
                                    height: 350,
                                    xaxis: { title: { text: 'N-gram' }, tickangle: -45 },
                                    yaxis: { title: { text: 'Impressions' } },
                                    margin: { l: 50, r: 20, t: 30, b: 120 },
                                }}
                                config={{ responsive: true }}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                )}

                {/* ───── Collapsible Data Table ───── */}
                {filtered.length > 0 && (
                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between" style={{ cursor : 'pointer' }}
                            onClick={() => setShowTable(!showTable)}>
                            <span className="fw-semibold">
                                {showTable ? 'v' : '>'} Show n-gram table
                            </span>
                            <span className="text-muted small">
                                {filtered.length} n-grams
                            </span>
                        </div>
                        {showTable && (
                            <>
                                <div className="card-body p-0">
                                    <div className="table-responsive" style={{ maxHeight: '500px' }}>
                                        <table className="table table-sm table-striped table-hover mb-0">
                                            <thead className="table-dark sticky-top">
                                                <tr>
                                                    <th>N-gram</th><th>N</th><th>Impressions</th><th>Clicks</th>
                                                    <th>Orders</th><th>Spend</th><th>Sales</th>
                                                    <th>CTR%</th><th>CVR%</th><th>CPC</th><th>RPC</th><th>ACOS%</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filtered.slice(0, 200).map((row, i) => (
                                                    <tr key={i}>
                                                        <td className="fw-semibold">{row.ngram}</td>
                                                        <td>{row.n}</td>
                                                        <td>{row.impressions.toLocaleString()}</td>
                                                        <td>{row.clicks.toLocaleString()}</td>
                                                        <td>{row.orders.toLocaleString()}</td>
                                                        <td>${row.spend.toFixed(2)}</td>
                                                        <td>${row.sales.toFixed(2)}</td>
                                                        <td>{row.ctr.toFixed(2)}</td>
                                                        <td>{row.cvr.toFixed(2)}</td>
                                                        <td>${row.cpc.toFixed(2)}</td>
                                                        <td>${row.rpc.toFixed(2)}</td>
                                                        <td>{row.acos.toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="card-footer text-muted">
                                    <small>Showing {Math.min(filtered.length, 200)} of {filtered.length} n-grams</small>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default NgramAnalysis;
